import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  Upload, 
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  Star,
  Heart,
  Share,
  Settings,
  Bell,
  Check,
  X,
  AlertCircle,
  Info,
  HelpCircle
} from 'lucide-react';
import Select from 'react-select';
import { normalizeOpticalSelectValue, buildOpticalSelectOptions } from '../../utils/opticalValues';

// Modern Card Components
export const ModernCard = ({ children, className = '', hover = true, glass = false, gradient = false, ...props }) => {
  const baseClasses = 'card';
  const variantClasses = glass ? ' card-glass' : gradient ? ' card-gradient' : '';
  const hoverClasses = hover ? ' card-hover' : '';
  
  return (
    <div className={`${baseClasses}${variantClasses}${hoverClasses} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Modern Button Components - Text-based and responsive
export const ModernButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  icon, 
  iconPosition = 'left',
  className = '',
  ...props 
}) => {
  const baseClasses = 'btn';
  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    success: 'btn-success',
    warning: 'btn-warning',
    danger: 'btn-danger',
    outline: 'btn-outline',
    ghost: 'btn-ghost'
  };
  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  };
  
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
  
  return (
    <button className={classes} {...props}>
      {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
      <span className="whitespace-nowrap">{children}</span>
      {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
    </button>
  );
};

// Modern Input Components
export const ModernInput = ({ 
  label, 
  error, 
  success, 
  icon, 
  className = '', 
  ...props 
}) => {
  const baseClasses = 'input';
  const stateClasses = error ? ' input-error' : success ? ' input-success' : '';
  
  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-neutral-700">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icon}
          </div>
        )}
        <input
          className={`${baseClasses}${stateClasses} ${icon ? 'pl-10' : ''} ${className}`}
          {...props}
        />
      </div>
      {error && (
        <p className="text-sm text-danger-600 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
      {success && (
        <p className="text-sm text-success-600 flex items-center">
          <Check className="w-4 h-4 mr-1" />
          {success}
        </p>
      )}
    </div>
  );
};

// Modern Select Component
export const ModernSelect = ({ 
  label, 
  options = [], 
  value,
  error, 
  className = '', 
  ...props 
}) => {
  // Map options to react-select format if needed
  const selectOptions = options.map(option =>
    option.value !== undefined && option.label !== undefined
      ? option
      : { value: option.id, label: option.name }
  );

  // Ensure value is in the correct format for react-select
  const selectValue = value && typeof value === 'object' && value.value !== undefined 
    ? value 
    : value && selectOptions.find(opt => String(opt.value).trim() === String(value).trim());

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-neutral-700">
          {label}
        </label>
      )}
      <Select
        classNamePrefix="modern-select"
        options={selectOptions}
        value={selectValue}
        isSearchable
        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        styles={{
          menu: base => ({ ...base, maxHeight: 300, overflowY: 'auto', scrollBehavior: 'smooth', zIndex: 99999 }),
          menuPortal: base => ({ ...base, zIndex: 99999 })
        }}
        {...props}
      />
      {error && (
        <p className="text-sm text-danger-600 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
};

// Modern Badge Component
export const ModernBadge = ({ 
  children, 
  variant = 'neutral', 
  icon, 
  className = '', 
  ...props 
}) => {
  const variantClasses = {
    primary: 'badge-primary',
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    neutral: 'badge-neutral'
  };
  
  return (
    <span className={`badge ${variantClasses[variant]} ${className}`} {...props}>
      {icon && <span className="mr-1">{icon}</span>}
      <span className="whitespace-nowrap">{children}</span>
    </span>
  );
};

// Modern Modal Component - Responsive
export const ModernModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  footer, 
  size = 'lg',
  className = '' 
}) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-4xl',
    xxl: 'max-w-6xl',
    wide: 'max-w-[min(96vw,1280px)]',
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content ${sizeClasses[size]} ${className} max-h-[95vh] overflow-y-auto w-full mx-4`} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="modal-header">
            <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-sm absolute top-4 right-4 inline-flex items-center gap-1.5 px-2 py-1"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Fermer</span>
            </button>
          </div>
        )}
        <div className="modal-body">
          {children}
        </div>
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// Modern Table Component - Responsive (memoized to avoid re-rendering when parent re-renders with same columns/data)
export const ModernTable = React.memo(function ModernTable({ columns = [], data = [], className = '', ...props }) {
  return (
    <div className="overflow-x-auto">
      <table className={`table ${className}`} {...props}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.headerClassName || undefined}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={row?.id != null ? String(row.id) : index}>
              {columns.map((column) => (
                <td key={column.key} className={column.className || undefined}>
                  {column.render ? column.render(row[column.key], row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

// Modern Loading Spinner
export const ModernLoadingSpinner = ({ size = 'md' }) => (
  <div className={`flex items-center justify-center animate-spin rounded-full border-4 border-blue-500 border-t-transparent ${size === 'lg' ? 'h-16 w-16' : size === 'sm' ? 'h-4 w-4' : 'h-8 w-8'}`}></div>
);

// Modern Stat Card
export const ModernStatCard = ({ 
  title, 
  value, 
  subtitle,
  change, 
  changeType = 'neutral', 
  icon, 
  variant = 'neutral',
  className = '' 
}) => {
  const variantClasses = {
    primary: 'stat-card-primary',
    success: 'stat-card-success',
    warning: 'stat-card-warning',
    danger: 'stat-card-danger',
    neutral: ''
  };
  
  const changeClasses = {
    positive: 'text-success-600',
    negative: 'text-danger-600',
    neutral: 'text-neutral-600'
  };
  
  return (
    <div className={`stat-card ${variantClasses[variant]} ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-neutral-600">{title}</p>
          <p className="text-2xl font-bold text-neutral-900">{value}</p>
          {subtitle && (
            <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>
          )}
          {change && (
            <p className={`text-sm font-medium ${changeClasses[changeType]}`}>
              {change}
            </p>
          )}
        </div>
        {icon && (
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

// Modern Search Bar - Responsive
export const ModernSearchBar = ({ 
  placeholder = 'Search...', 
  onSearch, 
  className = '',
  debounce = 300,
  ...props 
}) => {
  const [query, setQuery] = useState('');
  const debounceRef = useRef();

  // Debounce search effect
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch?.(query);
    }, debounce);
    return () => clearTimeout(debounceRef.current);
  }, [query, debounce, onSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearch?.(query);
  };

  const handleClear = () => {
    setQuery('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearch?.('');
  };

  return (
    <form onSubmit={handleSubmit} className={`relative ${className}`} autoComplete="off">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-neutral-400" />
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="input pl-10 pr-10 w-full"
        {...props}
      />
      {query && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600 focus:outline-none"
          tabIndex={-1}
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </form>
  );
};

// Modern Action Bar - Responsive
export const ModernActionBar = ({ 
  title, 
  actions = [], 
  className = '' 
}) => {
  return (
    <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white border-b border-neutral-200 space-y-4 sm:space-y-0 ${className}`}>
      <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action, index) => (
          <ModernButton
            key={index}
            variant={action.variant || 'primary'}
            size={action.size || 'sm'}
            icon={action.icon}
            onClick={action.onClick}
          >
            {action.label}
          </ModernButton>
        ))}
      </div>
    </div>
  );
};

// Modern Empty State
export const ModernEmptyState = ({ 
  icon, 
  title, 
  description, 
  action, 
  className = '' 
}) => {
  return (
    <div className={`text-center py-12 ${className}`}>
      {icon && (
        <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
      <p className="text-neutral-600 mb-6 max-w-md mx-auto">{description}</p>
      {action && (
        <ModernButton variant="primary" icon={action.icon} onClick={action.onClick}>
          {action.label}
        </ModernButton>
      )}
    </div>
  );
};

// Modern Notification
export const ModernSwitch = ({ 
  checked = false, 
  onChange, 
  disabled = false,
  className = '',
  ...props 
}) => {
  // Debug logging for switch component
  console.log('ModernSwitch render:', { checked, disabled, className });
  
  return (
    <label className={`relative inline-flex items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => {
          console.log('Switch onChange:', e.target.checked);
          onChange && onChange(e.target.checked);
        }}
        disabled={disabled}
        {...props}
      />
      <div className={`
        w-11 h-6 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-300 
        peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] 
        after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-neutral-300 
        after:border after:rounded-full after:h-5 after:w-5 after:transition-all 
        ${checked ? 'bg-green-500' : 'bg-gray-300'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
      `}></div>
      {/* Add a small indicator text */}
      <span className={`ml-2 text-xs font-medium ${checked ? 'text-green-600' : 'text-gray-500'}`}>
        {checked ? 'ON' : 'OFF'}
      </span>
    </label>
  );
};

export const ModernNotification = ({ 
  type = 'info', 
  title, 
  message, 
  onClose, 
  className = '' 
}) => {
  const typeClasses = {
    success: 'border-success-200 bg-success-50 text-success-800',
    warning: 'border-warning-200 bg-warning-50 text-warning-800',
    danger: 'border-danger-200 bg-danger-50 text-danger-800',
    info: 'border-primary-200 bg-primary-50 text-primary-800'
  };
  
  const typeIcons = {
    success: <Check className="w-5 h-5" />,
    warning: <AlertCircle className="w-5 h-5" />,
    danger: <X className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />
  };
  
  return (
    <div className={`border rounded-lg p-4 ${typeClasses[type]} ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3">
          {typeIcons[type]}
        </div>
        <div className="flex-1">
          {title && <h4 className="font-medium">{title}</h4>}
          {message && <p className="text-sm mt-1">{message}</p>}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 ml-3 btn btn-ghost btn-sm p-1"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Modern Optical Select Component for Sph/Cyl values
export const ModernOpticalSelect = ({ 
  label, 
  value,
  onChange,
  error, 
  className = '',
  placeholder = "Sélectionner...",
  ...props 
}) => {
  const opticalValues = buildOpticalSelectOptions();

  const normalizedValue = normalizeOpticalSelectValue(value);
  const currentValue = normalizedValue
    ? opticalValues.find((opt) => opt.value === normalizedValue)
    : null;

  const handleChange = (selectedOption) => {
    const newValue = selectedOption ? selectedOption.value : '';
    onChange({ target: { value: newValue } });
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-neutral-700">
          {label}
        </label>
      )}
      <Select
        classNamePrefix="modern-select"
        options={opticalValues}
        value={currentValue}
        onChange={handleChange}
        isSearchable
        isClearable
        placeholder={placeholder}
        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: '42px',
            borderColor: error ? '#ef4444' : state.isFocused ? '#b4243a' : '#d1d5db',
            borderWidth: '1px',
            borderRadius: '8px',
            boxShadow: state.isFocused ? `0 0 0 1px #b4243a` : 'none',
            '&:hover': {
              borderColor: error ? '#ef4444' : '#b4243a'
            }
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
              ? '#b4243a' 
              : state.isFocused 
                ? '#f3f4f6' 
                : 'white',
            color: state.isSelected ? 'white' : '#374151',
            '&:hover': {
              backgroundColor: state.isSelected ? '#b4243a' : '#f3f4f6'
            }
          }),
          menu: (base) => ({ 
            ...base, 
            maxHeight: 300, 
            overflowY: 'auto', 
            scrollBehavior: 'smooth', 
            zIndex: 9999 
          }),
          menuPortal: (base) => ({ 
            ...base, 
            zIndex: 9999 
          }),
          placeholder: (base) => ({
            ...base,
            color: '#9ca3af'
          })
        }}
        {...props}
      />
      {error && (
        <p className="text-sm text-danger-600 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
};

export default {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernOpticalSelect,
  ModernBadge,
  ModernModal,
  ModernTable,
  ModernLoadingSpinner,
  ModernStatCard,
  ModernSearchBar,
  ModernActionBar,
  ModernEmptyState,
  ModernNotification,
  ModernSwitch
}; 